import InfoRounded from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { PageSection } from '@openx/components-core/lib/PageSection/PageSection';
import { Table, type TableName } from '@openx/components-table/Table';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TddSyncExpandableTableRow } from '../../components/TddSyncExpandableTableRow';
import { type ErrorRow, useTddErrorTableColumns } from '../hooks/useTddErrorTableColumns';
import type { ParsedPdpError } from '../utils/parsePdpError';

type SyncErrorsSectionProps = {
  errors: ParsedPdpError[];
  lastErrorDatetime: string | undefined;
  tableName: TableName;
};

export const SyncErrorsSection = ({ errors, lastErrorDatetime, tableName }: SyncErrorsSectionProps) => {
  const { t } = useTranslation();

  const errorsWithIndex: ErrorRow[] = useMemo(
    () =>
      errors.map((error, index) => ({
        ...error,
        index: index + 1,
      })),
    [errors]
  );

  const { columns, isForcedExpanded, isCollapsableJsonView, toggleRowExpansion } =
    useTddErrorTableColumns(errorsWithIndex);

  if (errorsWithIndex.length === 0 || !lastErrorDatetime)
    return (
      <PageSection title={t('Errors')}>
        <Grid size={RIGHT_COLUMN_WIDTH + 1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoRounded fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography>{t('No errors found')}</Typography>
          </Box>
        </Grid>
      </PageSection>
    );

  return (
    <PageSection title={t('Errors')}>
      <Grid size={9}>
        <Box
          sx={{
            borderBottom: 'none',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyItems: 'center',
              gap: 3,
              padding: theme => theme.spacing(2, 3.5),
              '& > span': {
                color: 'text.secondary',
                ml: 1,
              },
            }}
          >
            <Typography variant="h3">{t('Errors List')}</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body1">{format(new Date(lastErrorDatetime), 'yyyy-MM-dd HH:mm:ss')}</Typography>

              <Tooltip title={`${t('The last time the error(s) occurred.')}`} placement="top">
                <IconButton>
                  <InfoRounded />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Table
            data={errorsWithIndex}
            columns={columns}
            criteria={{}}
            onCriteriaChange={undefined}
            loading={false}
            name={tableName}
            isForcedExpanded={isForcedExpanded}
            renderCustomRow={(rowProps, isForcedExpandedFn) => (
              <TddSyncExpandableTableRow
                isCollapsableJsonView={isCollapsableJsonView}
                isForcedExpanded={isForcedExpandedFn}
                rowProps={rowProps}
                toggleRowExpansion={toggleRowExpansion}
              />
            )}
            enableTableHeaderCustomization={false}
          />
        </Box>
      </Grid>
    </PageSection>
  );
};
